import { useEffect, useState } from 'react';

import { BottomSheet } from '~/modules/elements/akui/bottom-sheet';
import events from '~/modules/game-events/game-events';
import { useEventListener } from '~/modules/game-events/hooks';
import { LOCAL_PLAYER_NUMBERS, PlayerNumber } from '~/modules/players/player-number';
import { serverRpc } from '~/modules/remote-mic/network/client';
import { useClientHandler } from '~/modules/remote-mic/network/client/hooks/use-client-handler';
import ColourTiles from '~/routes/remote-mic/components/colour-tiles';
import { usePhoneToast } from '~/routes/remote-mic/components/phone-toast-context';
import { playerColourName } from '~/routes/remote-mic/components/player-colour';
import useColourOccupants from '~/routes/remote-mic/components/use-colour-occupants';
import usePermissions from '~/routes/remote-mic/hooks/use-permissions';
import useRemoteMicId from '~/routes/remote-mic/hooks/use-remote-mic-id';
import useRemoteMicName from '~/routes/remote-mic/hooks/use-remote-mic-name';

// The television asks when it opens Setup microphones. Only a phone that has joined with a singer
// and holds no slot answers; a read-only one never picks a colour.
export default function ColourSheet({ joined }: { joined: boolean }) {
  const [open, setOpen] = useState(false);
  const micId = useRemoteMicId();
  const permissions = usePermissions();
  const { name, hasStoredName } = useRemoteMicName();
  const [playerNumber] = useEventListener(events.remoteMicPlayerSet, true) ?? [null];
  const [connectionStatus] = useEventListener(events.karaokeConnectionStatusChange, true) ?? ['uninitialised'];
  const occupants = useColourOccupants(micId);
  const flash = usePhoneToast();

  const wanted = joined && connectionStatus === 'connected' && playerNumber === null;
  useClientHandler('notifyPlayerSettingsOpen', () => {
    if (wanted && hasStoredName && permissions === 'write') setOpen(true);
  });
  // Seated some other way, or cut off from the television, while the question was still up.
  useEffect(() => {
    if (!wanted) setOpen(false);
  }, [wanted]);

  const pick = (number: PlayerNumber) => {
    if (micId) {
      serverRpc.players.requestMicSelect(micId, number).catch(() => flash('The TV did not take the colour'));
    }
    setOpen(false);
  };

  return (
    <BottomSheet open={open} onClose={() => setOpen(false)} title="Pick your colour">
      <ColourTiles
        playerNumbers={LOCAL_PLAYER_NUMBERS}
        selected={playerNumber}
        ownName={name}
        occupants={occupants}
        onPick={pick}
        onBlocked={(number, occupant) => flash(`${occupant} has ${playerColourName(number)}`)}
      />
    </BottomSheet>
  );
}
